import clsx from 'clsx';

import { SelectOptionValue, SelectOption } from '@stern-ui/ui';

import { FieldErrors, useFieldContext } from '../';
import { SelectV2, selectSizes, Label } from '../../Elements';

type SelectFieldProps = {
	label: string;
	labelVisible?: boolean;
	labelClassName?: string;
	options: SelectOption[] | [];
	isMulti?: boolean;
	required?: boolean;
	isCompact?: boolean;
	showSelectAll?: boolean;
	isDisabled?: boolean;
	disabledHelpText?: string;
	customLabel?: (data: SelectOption) => React.ReactElement;
	placeholder?: string;
	containerClass?: string;
	size?: keyof typeof selectSizes;
	selectClassNames?: { [key: string]: string };
	SelectTooltip?: React.ReactNode;
};

export const SelectField = ({
	label,
	labelVisible = true,
	options,
	isMulti,
	required,
	isCompact,
	showSelectAll,
	isDisabled,
	disabledHelpText,
	customLabel,
	placeholder,
	containerClass,
	size,
	selectClassNames,
	labelClassName,
	SelectTooltip = null,
}: SelectFieldProps) => {
	const field = useFieldContext<string | string[] | SelectOptionValue[]>();

	const checkValue = (option: SelectOption, val: unknown) => {
		// check if the option matches the value considering it could be an object or just a plain value
		if (typeof val === 'object' && val !== null) {
			return Object.values(val).includes((option.value as unknown as SelectOptionValue).id);
		}
		return val === option.value;
	};

	const generateMultiSelctValueArr = (options: SelectOption[], isSingleValue: boolean) => {
		// this function generates an array of options that match the value(s) in the field state
		// It is used for single and multi select fields that have either single values or { id, name } objects as values

		return options.reduce((result: SelectOption[], option) => {
			option?.options?.map((o) => {
				if (isSingleValue) {
					if (checkValue(o, field.state.value)) {
						result.push(o);
					}
				} else if (
					field.state.value &&
					(field.state.value as SelectOptionValue[]).some((val) => checkValue(o, val))
				) {
					result.push(o);
				}
			});
			return result;
		}, []);
	};

	// Map the value from form state to the corresponding option object
	const getSelectedOption = () => {
		if (options) {
			// check if this a mutli select or single select select field
			if (isMulti && Array.isArray(field.state.value)) {
				// Is a multi select, so we need to return an array of options that match the values in the field state
				// check if options have groups
				if (options[0]?.options) {
					// does have groups, so use the group options to check for value
					return generateMultiSelctValueArr(options, false);
				} else {
					// doesn't have groups, so check option values directly
					return options.filter(
						(option) =>
							field.state.value?.length >= 1 &&
							(field.state.value as SelectOptionValue[])?.some(
								(elm: SelectOptionValue) => checkValue(option, elm)
							)
					);
				}
			} else {
				// Is a single select, so we need to return the single option that matches the value in the field state
				// check if options have groups
				if (options[0]?.options) {
					// does have groups, so use the group options to check for value
					return generateMultiSelctValueArr(options, true);
				} else {
					const selectedOption = options.find(
						(option) => field.state.value && checkValue(option, field.state.value)
					);
					// When no value, React-select requires the value to be null rather than undefined
					return selectedOption ? [selectedOption] : null;
				}
			}
		} else {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			return isMulti ? [] : ('' as any);
		}
	};

	// Pass only the value to TanStack Forms
	const handleOnChange = (selected: SelectOption | SelectOption[]) => {
		if (isMulti) {
			const values = Array.isArray(selected) ? selected.map((opt) => opt.value) : [];
			field.handleChange(values as string[]);
		} else {
			field.handleChange(((selected as SelectOption)?.value ?? '') as string);
		}
	};

	// hide the error messages until the field has been touched
	const hideErrors = !field.state.meta.isTouched;

	return (
		<div className={clsx('flex flex-col select-field-container', containerClass)}>
			<div className='flex items-center'>
				<Label
					htmlFor={field.name}
					label={label}
					required={required}
					className={clsx('mb-1', labelVisible ? '' : 'sr-only', labelClassName)}
					disabled={isDisabled}
				/>
				{SelectTooltip as React.ReactNode}
			</div>
			<SelectV2
				name={field.name}
				options={options}
				handleChange={handleOnChange}
				value={getSelectedOption()} // Pass option object(s) for label display
				isMulti={isMulti}
				isCompact={isCompact}
				placeholder={placeholder || 'Select'}
				showSelectAll={showSelectAll}
				isDisabled={isDisabled}
				disabledHelpText={disabledHelpText}
				customLabel={customLabel}
				size={size}
				selectClassNames={selectClassNames}
				onBlur={() => {
					// set field as touched
					field.handleBlur();
				}}
			/>
			<FieldErrors meta={field.state.meta} name={field.name} hideErrors={hideErrors} />
		</div>
	);
};
